import './docPreviewRow.scss';
import {
    useStateDocumentHighlights,
    useStateIsDocumentLoading,
    useStateIsPreviewHeldForHits,
    useStatePreviewDocument,
    useStateQuery,
    useStateResultFor,
} from '../../../search-hooks';
import React, {useCallback, useEffect} from 'react';
import {PdfViewer} from '../../../../common/pdf-viewer/PdfViewer';
import {TextViewer} from '../../../../common/pdf-viewer/TextViewer';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {ProdDocument, ProductDocument} from '../../../search-types';
import {PreviewDocument} from './docPreviewRow-types';
import {ImageViewer} from '../../../../common/image-viewer/ImageViewer';
import {doFetchDocumentHighlights, doFetchPreviewFile} from '../../../duck/search-actions';
import {ViewerToolbar} from '../../../../common/pdf-viewer/ViewerToolbar';
import {InDocumentSearchProps} from '../../../../common/pdf-viewer/PdfViewer.types';
import {getErrorMessage} from '../../../../utils/utils';
import {Spinner} from '../../../../common/spinner/spinner';

const IMAGE_EXTENSIONS: string[] = ['bmp', 'gif', 'jpeg', 'jpg', 'pbm', 'pcd', 'pct', 'pcx', 'pgm', 'png', 'ppm',
    'psd', 'pxl', 'ras', 'tga', 'tif', 'tiff', 'xbm', 'xpm', 'cdr', 'cgm', 'cmx',
    'dxf', 'emf', 'eps', 'fodg', 'met',];

const isPdfFile = (blob: Blob) => blob.type === 'application/pdf';

const isFileExtensionPartOfList = (selectedDocument: ProductDocument, extensionsList: string[]): boolean => {
    const contentType = selectedDocument.metadata?.contentType_string?.split('/').pop();
    if (!contentType) {
        return false;
    }
    return extensionsList.includes(contentType?.toLowerCase());
};

type DocPreviewRowProps = {
    selectedDocument: ProdDocument | undefined;
};

export const DocPreviewRow = ({selectedDocument}: DocPreviewRowProps): React.JSX.Element | null => {
    const dispatch = useDispatch();
    const query = useStateQuery();
    const resultFor = useStateResultFor();
    const settingsContainerName = useSelector((state: DefaultRootState) => state.settings.containerName);
    const isDocumentLoading = useStateIsDocumentLoading();
    const isPreviewHeldForHits = useStateIsPreviewHeldForHits();
    const isLoading = isDocumentLoading || isPreviewHeldForHits;
    const previewDocument = useStatePreviewDocument();
    const documentHighlights = useStateDocumentHighlights();

    const [inDocumentSearchProps, setInDocumentSearchProps] = React.useState<InDocumentSearchProps>();

    // The image viewer fetches its own file, so a second fetch here would be wasted.
    const isImage = selectedDocument !== undefined && isFileExtensionPartOfList(selectedDocument, IMAGE_EXTENSIONS);

    useEffect(() => {
        if (selectedDocument !== undefined && !isImage) {
            dispatch(doFetchPreviewFile(selectedDocument));
        }
    }, [dispatch, isImage, selectedDocument]);

    // The main search's hits. The viewer waits for them, so the document opens on the first hit instead of
    // jumping there once they arrive.
    useEffect(() => {
        if (selectedDocument !== undefined && !isImage) {
            dispatch(doFetchDocumentHighlights(
                selectedDocument, resultFor, query, settingsContainerName, {holdPreview: true},
            ));
        }
    }, [dispatch, isImage, query, resultFor, selectedDocument, settingsContainerName]);

    const renderPdfViewer = useCallback((): React.JSX.Element => {
        return (
            <PdfViewer
                file={previewDocument?.documentBlob}
                onInDocumentSearchPropsChange={setInDocumentSearchProps}
            />
        );
    }, [previewDocument?.documentBlob]);

    const renderImageViewer = useCallback(
        (selectedDocument: ProductDocument): React.JSX.Element => {
            return <ImageViewer imageLink={selectedDocument.metadata.previewLink_string}/>;
        },
        [],
    );

    const renderTextViewer = useCallback(
        (selectedDocument: ProductDocument, previewDocument: PreviewDocument | undefined): React.JSX.Element => {
            const titleHtml = documentHighlights?.title ? documentHighlights.title : selectedDocument.title;
            let contentHtml: string;
            if (documentHighlights?.content) {
                contentHtml = documentHighlights.content;
            } else {
                contentHtml = selectedDocument?.content
                    ? selectedDocument.content
                    : getErrorMessage(previewDocument?.error);
            }
            return <TextViewer title={titleHtml} content={contentHtml}
                               onInDocumentSearchPropsChange={setInDocumentSearchProps}/>;
        },
        [documentHighlights],
    );

    const renderViewer = useCallback((): React.JSX.Element | null => {
        if (selectedDocument) {
            let renderViewer: React.JSX.Element;
            if (isLoading) {
                renderViewer = <Spinner/>;
            } else if (isFileExtensionPartOfList(selectedDocument, IMAGE_EXTENSIONS)) {
                renderViewer = renderImageViewer(selectedDocument);
            } else if (previewDocument?.documentBlob && isPdfFile(previewDocument.documentBlob)) {
                renderViewer = renderPdfViewer();
            } else {
                renderViewer = renderTextViewer(selectedDocument, previewDocument);
            }
            return (
                <div id="preview_content">
                    <ViewerToolbar inDocumentSearchProps={inDocumentSearchProps}/>
                    <div className="details-preview-doc">{renderViewer}</div>
                </div>
            );
        }
        return <></>;
    }, [selectedDocument, isLoading, previewDocument, renderTextViewer, inDocumentSearchProps, renderImageViewer, renderPdfViewer]);

    return <>{renderViewer()}</>;
};
